import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {WHATSAPP_MAX_TEXT_LENGTH} from '../../../../pages/branding/whatsAppScreen/whatsAppValidation';
import * as css from './WhatsAppEditorWarningBadges.css';
import CheckMark from '../../../../assets/icons/checkMark';
import WarningIcon from '../../../../assets/icons/WarningIcon';
import LineIcon from '../../../../assets/icons/LineIcon';
import {getToken} from '@tickchak/react-share/app';

type BadgeVariant = 'error' | 'warning' | 'success';

interface WhatsAppEditorWarningBadgesProps {
    totalCharacters: number;
    maxCharacters?: number;
    filledParagraphsCount: number;
    totalParagraphsCount: number;
    hasDisabledVariables?: boolean;
}

type BadgeConfig = {
    type: BadgeVariant;
    text: string;
    counter?: {filled: number; total: number};
    icon: React.ReactNode;
};

function WhatsAppEditorWarningBadges({
    totalCharacters,
    maxCharacters = WHATSAPP_MAX_TEXT_LENGTH,
    filledParagraphsCount,
    totalParagraphsCount,
    hasDisabledVariables = false
}: WhatsAppEditorWarningBadgesProps) {
    const {t} = useTranslation('whatsapp');
    const {t: tVariables} = useTranslation('variables');

    const badgeConfig = useMemo((): BadgeConfig | null => {
        if(totalCharacters > maxCharacters) {
            return {
                type: 'error',
                text: `${t('characterLimitExceeded')} ${maxCharacters} ${t('characters')}`,
                icon: <LineIcon width={15} height={2} />
            };
        }
        if(hasDisabledVariables) {
            return {
                type: 'error',
                text: tVariables('warningDisabled'),
                icon: <WarningIcon fill={getToken('color.state.error.main')} width={20} height={20} />
            };
        }
        if(filledParagraphsCount < totalParagraphsCount && totalParagraphsCount > 0) {
            return {
                type: 'warning',
                text: t('emptyParagraphsCount'),
                counter: {filled: totalParagraphsCount - filledParagraphsCount, total: totalParagraphsCount},
                icon: <WarningIcon width={20} height={20} />
            };
        }
        if(totalParagraphsCount > 0) {
            return {
                type: 'success',
                text: t('allParagraphsFilled'),
                counter: {filled: filledParagraphsCount, total: totalParagraphsCount},
                icon: <CheckMark width={12} height={12} />
            };
        }
        return null;
    }, [totalCharacters, maxCharacters, hasDisabledVariables, filledParagraphsCount, totalParagraphsCount]);

    if(!badgeConfig) {
        return null;
    }

    const {type, text, counter, icon} = badgeConfig;

    return (
        <css.badge variant={type}>
            <css.badgeIcon variant={type}>{icon}</css.badgeIcon>
            {counter && (
                <css.badgeCounter variant={type}>
                    {counter.total} / {counter.filled}
                </css.badgeCounter>
            )}
            <css.badgeText variant={type}>{text}</css.badgeText>
        </css.badge>
    );
}

export default React.memo(WhatsAppEditorWarningBadges, (prev, next) => {
    return prev.totalCharacters === next.totalCharacters &&
        prev.filledParagraphsCount === next.filledParagraphsCount &&
        prev.totalParagraphsCount === next.totalParagraphsCount &&
        prev.hasDisabledVariables === next.hasDisabledVariables;
});
